/**
 * AuditLog — User's own action trail
 *
 * Customer-facing audit log so they can verify CARTIE's claims with their
 * own eyes. Required for SOC 2, GDPR, and enterprise procurement.
 *
 * Features:
 *   - List the user's own events (paginated)
 *   - Filter by action prefix
 *   - Self-test button to verify writes work (proves the system is live)
 *   - CSV export
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  FileText, Download, Play, RefreshCw, Filter, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 50;

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset };
      if (filter) params.action_prefix = filter;
      const r = await axios.get(`${API}/me/audit-log`, { headers: headers(), params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [offset, filter]);

  const runSelfTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/me/audit-log/self-test`, {}, { headers: headers() });
      setTestResult({ ok: r.data?.ok, msg: r.data?.message });
      // Reload after a short delay so the new event appears
      setTimeout(() => { setOffset(0); load(); }, 600);
    } catch (e) {
      setTestResult({ ok: false, msg: e?.response?.data?.detail || 'Self-test failed' });
    } finally {
      setTesting(false);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/me/audit-log/export.csv`, {
        headers: headers(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cartie-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // no-op
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE));
  const currentPage = Math.floor(offset / PAGE) + 1;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>Audit Log · CARTIE</title>
      </Helmet>

      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur">
        <Link to="/dashboard" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="audit-back">
          ← Dashboard
        </Link>
        <div className="text-xs font-mono text-slate-400">/api/me/audit-log</div>
      </header>

      <section className="px-4 py-10 max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-3">
              <FileText className="w-3 h-3" /> Your audit trail
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight" data-testid="audit-h1">Every action — yours and ours.</h1>
            <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
              Each entry is immutable, retained 365 days, and visible only to you.
              Required by GDPR Article 30 and SOC 2 CC7. Export as CSV anytime.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={runSelfTest}
              disabled={testing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs transition disabled:opacity-50"
              data-testid="audit-self-test-btn"
            >
              <Play className="w-3.5 h-3.5" /> {testing ? 'Testing…' : 'Run self-test'}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:border-emerald-500/40 text-white font-semibold text-xs transition"
              data-testid="audit-export-btn"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              type="button"
              onClick={() => { setOffset(0); load(); }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:border-emerald-500/40 text-white font-semibold text-xs transition"
              data-testid="audit-refresh-btn"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {testResult && (
          <div className={`mt-4 rounded-lg p-3 text-xs border ${testResult.ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-rose-500/10 border-rose-500/30 text-rose-300'}`} data-testid="audit-self-test-result">
            {testResult.msg}
          </div>
        )}

        {/* Filter bar */}
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2" data-testid="audit-filter-bar">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <input
            value={filter}
            onChange={(e) => { setOffset(0); setFilter(e.target.value); }}
            placeholder="Filter by action prefix (e.g. proxy.key.)"
            className="flex-1 bg-transparent text-sm text-slate-200 placeholder-slate-500 focus:outline-none font-mono"
            data-testid="audit-filter-input"
          />
        </div>

        {/* Events list */}
        <div className="mt-4 rounded-xl border border-white/10 bg-slate-900/30 overflow-hidden" data-testid="audit-events-list">
          {loading && items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="audit-empty-state">
              No events yet. Click <strong className="text-emerald-300">Run self-test</strong> to create one.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((evt, i) => (
                <EventRow key={i} evt={evt} testid={`audit-event-row-${i}`} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-xs text-slate-400" data-testid="audit-pagination">
          <div>{total.toLocaleString()} events · page {currentPage} of {pageCount}</div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
              disabled={offset === 0}
              className="p-1.5 rounded border border-white/10 hover:border-emerald-500/40 disabled:opacity-30 transition"
              data-testid="audit-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setOffset(offset + PAGE)}
              disabled={offset + PAGE >= total}
              className="p-1.5 rounded border border-white/10 hover:border-emerald-500/40 disabled:opacity-30 transition"
              data-testid="audit-next-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function EventRow({ evt, testid }) {
  const ts = new Date(evt.ts).toLocaleString();
  return (
    <div className="px-4 py-3 hover:bg-white/5 transition-colors" data-testid={testid}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[11px] text-slate-500 font-mono shrink-0">{ts}</span>
        <code className="text-xs text-emerald-300 bg-slate-950 px-1.5 py-0.5 rounded font-mono">{evt.action}</code>
        {evt.target_type && (
          <span className="text-[11px] text-slate-400">
            on <code className="text-slate-300 font-mono">{evt.target_type}</code>
            {evt.target_id && <code className="text-slate-500 font-mono ml-1">#{String(evt.target_id).slice(0, 8)}</code>}
          </span>
        )}
        {evt.ip_address && (
          <span className="text-[10px] text-slate-500 font-mono">{evt.ip_address}</span>
        )}
      </div>
      {evt.metadata && Object.keys(evt.metadata).length > 0 && (
        <details className="mt-1.5">
          <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-emerald-300">metadata</summary>
          <pre className="mt-1 text-[10px] text-slate-400 bg-slate-950 border border-white/10 rounded p-2 overflow-x-auto font-mono">
            {JSON.stringify(evt.metadata, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
